import React, { useState } from 'react';
import Modal from './Modal';
import { settingsAPI } from '../../services/api';
import { useAuth } from '../../services/AuthContext';

const EditAvatarModal = ({ isOpen, onClose, currentAvatar }) => {
  const { user, updateUser } = useAuth();
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(currentAvatar || user?.avatarUrl);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Basic validation
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }

      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setError('');
      setSuccess('');
    }
  };

  const handleSave = async () => {
    if (!selectedFile) {
      setError('Please select an image first');
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await settingsAPI.uploadAvatar(selectedFile);
      const { avatarUrl } = response.data;
      
      // Update global user state (this will update all UIs using the user object)
      updateUser({ avatarUrl });
      
      setSuccess('Avatar updated successfully!');
      
      // Close modal after a short delay to show success
      setTimeout(() => {
        onClose();
        setSuccess('');
        setSelectedFile(null);
      }, 1500);

    } catch (err) {
      console.error('Error uploading avatar:', err);
      setError(err.response?.data?.message || 'Failed to upload avatar. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit AI Avatar">
      <div className="space-y-6">
        <p className="text-sm text-gray-600">
          Upload a new avatar image for your AI Persona. This image will represent your Ally when conversing with clients.
        </p>

        <div className="flex flex-col items-center gap-6">
          <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-gray-100 shadow-md relative group">
            <img 
              src={previewUrl ? (previewUrl.startsWith('blob:') ? previewUrl : `${process.env.REACT_APP_API_URL}${previewUrl}`) : "https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png"} 
              alt="Avatar Preview" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <label className="cursor-pointer flex flex-col items-center">
                <span className="material-symbols-outlined text-white mb-1">photo_camera</span>
                <span className="text-white text-xs font-bold">Change</span>
                <input type="file" className="hidden" accept="image/*" onChange={handleFileChange} />
              </label>
            </div>
          </div>
          
          <div className="w-full">
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2 hidden">Upload Image</label>
            <input 
              className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none file:mr-4 file:py-2.5 file:px-4 file:rounded-l-lg file:border-0 file:text-sm file:font-bold file:bg-[var(--tangerine)] file:text-white hover:file:bg-[#d97700] transition-colors" 
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              disabled={isSaving}
            />
            <p className="mt-1 text-xs text-gray-500" id="file_input_help">PNG, JPG or GIF.</p>
            
            {error && <p className="mt-2 text-xs text-red-600 font-bold">{error}</p>}
            {success && <p className="mt-2 text-xs text-green-600 font-bold">{success}</p>}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <button 
            onClick={onClose}
            className="px-5 py-2 text-sm font-bold text-gray-600 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button 
            onClick={handleSave}
            disabled={isSaving || !selectedFile}
            className={`px-5 py-2 text-sm font-bold text-white bg-[var(--tangerine)] rounded-xl transition-all shadow-md shadow-orange-500/20 ${(isSaving || !selectedFile) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#d97700] hover:-translate-y-0.5'}`}
          >
            {isSaving ? 'Uploading...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default EditAvatarModal;
